import { cx } from '../cx';

/**
 * Our icon set. Hand-drawn hairline paths (1.5px stroke, 24 grid, round caps)
 * instead of an icon dependency: the editorial look needs ONE stroke weight
 * everywhere, and a 1,500-glyph library invites drift and grows the bundle for
 * the twenty glyphs a CRM actually uses.
 *
 * Adding a glyph = one entry. `IconName` is derived, so a typo at a call site
 * is a type error.
 *
 * EVERY PATH IS CENTRED IN THE GRID, and that is a measured property, not an
 * intention. A glyph whose ink sits off-centre cannot be rescued by the box it
 * is placed in: centring the 24x24 viewBox inside a 40px button centres the
 * BOX, so a path drawn 1.4 units to the left stays 1.4 units to the left in
 * every button, row and menu in the product. Seventeen of these were off when
 * WP6b measured them with Chromium's own getBBox().
 *
 * THE RULE: the path's geometric bounding box is centred on (12,12), within
 * 1/8 of a grid unit. The uniform 1.5 stroke with round caps grows the box by
 * 0.75 on every side, so it moves the edges and never the centre - which is
 * why the geometric box is the honest thing to measure.
 *
 * The two dot-based glyphs (`more-horizontal`, `grip`) measure 0.005 off,
 * which is the `h.01` zero-length-dash trick and not a drawing error.
 */
const ICON_PATHS = {
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6L6 18',
  // Was centred on (12.75, 12.75): the whole magnifier sat low and right.
  search: 'M16 10.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0M14.4 14.4L19 19',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  // The four chevrons each sat half a unit past centre in their own direction,
  // which is why a row of them (table, cards, breadcrumbs) never lined up.
  'chevron-down': 'M6 9l6 6 6-6',
  'chevron-up': 'M6 15l6-6 6 6',
  'chevron-right': 'M9 6l6 6-6 6',
  'chevron-left': 'M15 6l-6 6 6 6',
  check: 'M5 12.5l4.5 4.5L19 7',
  'more-horizontal': 'M6 12h.01M12 12h.01M18 12h.01',
  'arrow-up': 'M12 19V5M6 11l6-6 6 6',
  'arrow-down': 'M12 5v14M6 13l6 6 6-6',
  // Up beside down (iOS arrow.up.arrow.down): both shafts span 5..19, heads 3.5
  // per side, so the box is 5..19 x 5..19 and sits on (12,12) exactly.
  sort: 'M8.5 19V5M5 8.5L8.5 5L12 8.5M15.5 5v14M12 15.5l3.5 3.5 3.5-3.5',
  sun: 'M12 4.5V2.5M12 21.5v-2M4.5 12h-2M21.5 12h-2M6.3 6.3L4.9 4.9M19.1 19.1l-1.4-1.4M17.7 6.3l1.4-1.4M4.9 19.1l1.4-1.4M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0',
  // Same crescent, moved 0.3 up and right onto the centre.
  moon: 'M20.3 14.4A8.6 8.6 0 0 1 9.6 3.7 8.5 8.5 0 1 0 20.3 14.4z',
  table: 'M4 5h16v14H4zM4 10h16M4 15h16M9.5 5v14',
  board: 'M4 5h4v14H4zM10 5h4v10h-4zM16 5h4v7h-4z',
  settings: 'M4 7h10M18 7h2M4 17h2M10 17h10M16 4.5v5M8 14.5v5',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8M4.5 20a7.5 7.5 0 0 1 15 0',
  // The worst of the set: 1.37 units left of centre, because the second figure
  // was drawn as an afterthought on the right. Redrawn as one composition.
  users:
    'M9.5 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M3.5 19.5a6 6 0 0 1 12 0M16.4 5.2a3.4 3.4 0 0 1 0 6.6M17.6 14.4a6 6 0 0 1 2.9 5.1',
  phone:
    'M6.25 4.25h3l1.5 4-2 1.5a12 12 0 0 0 5.5 5.5L15.75 13.25l4 1.5v3a2 2 0 0 1-2.2 2C9.75 19.75 4.25 14.25 4.25 6.45A2 2 0 0 1 6.25 4.25z',
  mail: 'M4 6h16v12H4zM4.5 7l7.5 5.5L19.5 7',
  link: 'M10.5 13.5a4 4 0 0 0 5.6 0l2.4-2.4a4 4 0 1 0-5.6-5.6l-1 1M13.5 10.5a4 4 0 0 0-5.6 0l-2.4 2.4a4 4 0 1 0 5.6 5.6l1-1',
  // The two hanger ticks reached higher than the body reached low.
  calendar: 'M4 6h16v14H4zM4 10h16M8 4V7M16 4V7',
  building: 'M5 19.5V4.5h9v15M14 9.5h5v10M8 7.5h3M8 11.5h3M8 15.5h3M17 12.5h1M17 16.5h1',
  briefcase: 'M4 8h16v11H4zM9 8V5h6v3M4 13h16',
  // Both sparkles moved as one group; the pair keeps its spacing.
  agent:
    'M10.5 3.5l1.6 4.4L16.5 9.5l-4.4 1.6L10.5 15.5l-1.6-4.4L4.5 9.5l4.4-1.6zM16.5 14.5l.8 2.2L19.5 17.5l-2.2.8L16.5 20.5l-.8-2.2L13.5 17.5l2.2-.8z',
  // Apex 4.5 -> 5 so the triangle spans 5..19 and centres without moving its base.
  warning: 'M12 5L21 19H3zM12 10v4M12 16.5h.01',
  blocked: 'M12 3.5l7 2.5v6c0 4-3 7-7 8.5-4-1.5-7-4.5-7-8.5V6zM12 8.5v4M12 15.5h.01',
  success: 'M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16M8.5 12.2l2.4 2.3L15.5 10',
  info: 'M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16M12 11v5M12 8.2h.01',
  clock: 'M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16M12 8v4.5l3 2',
  eye: 'M2.5 12S6 6.5 12 6.5 21.5 12 21.5 12 18 17.5 12 17.5 2.5 12 2.5 12M12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5',
  'eye-off':
    'M4 4l16 16M9.7 9.9A2.5 2.5 0 0 0 12 14.5c.6 0 1.2-.2 1.7-.6M6.6 7.7C4 9.4 2.5 12 2.5 12S6 17.5 12 17.5c1.4 0 2.7-.3 3.8-.8M17.7 15.1c2.3-1.6 3.8-3.1 3.8-3.1S18 6.5 12 6.5c-.6 0-1.1 0-1.6.1',
  // Can shortened by half a unit: the lid overhung the top by exactly that.
  trash: 'M5 7h14M9.5 7V4.5h5V7M6.5 7l1 12.5h9l1-12.5M10.5 10.5v6M13.5 10.5v6',
  pencil: 'M4.45 19.8l1-4.5L15.95 4.8a2.1 2.1 0 0 1 3 3L8.45 18.3z',
  grip: 'M9.5 6h.01M9.5 12h.01M9.5 18h.01M14.5 6h.01M14.5 12h.01M14.5 18h.01',
  home: 'M4 11l8-6.5 8 6.5v8.5h-5v-6H9v6H4z',
  filter: 'M4 6h16l-6 7v5l-4-2v-3z',
  external: 'M14 5h5v5M19 5l-7.5 7.5M18 14v5H5V6h5',
  // The arrow stopped at 17 while the door started at 5, hanging the glyph left.
  'log-out': 'M9.5 5H5v14h4.5M15.5 8.5l3.5 3.5-3.5 3.5M19 12H11',
  drag: 'M12 3.5l2.5 2.5h-5zM12 20.5L9.5 18h5zM3.5 12L6 9.5v5zM20.5 12L18 14.5v-5z',
  // Heartbeat trace: spans 3..21 x 6..18, so the box sits on (12,12) exactly.
  activity: 'M3 12h4l2.5-6 5 12 2.5-6h4',
  'credit-card': 'M3 6h18v12H3zM3 10h18',
  // Medal disc on (12,9), ribbon legs to y=20: box 7..17 x 4..20, centred.
  award: 'M17 9a5 5 0 1 1-10 0 5 5 0 0 1 10 0M9.5 13.2L8 20l4-2.2L16 20l-1.5-6.8',
  /**
   * A TRIGGER (workflow-builder.md D42). Bounding box 7..17 x 3..21, so the geometric
   * centre is (12,12) exactly - the rule this file's header states and WP6b measured
   * seventeen glyphs against.
   */
  bolt: 'M13 3L7 13h4l-1 8 7-10h-4z',
  /**
   * RUN NOW. Box 7..17 x 5.5..18.5, so the geometric centre is (12,12) exactly. The rule
   * this file measures by is the GEOMETRIC box and not the optical one - the uniform 1.5
   * stroke with round caps grows every edge by 0.75 and therefore moves the edges without
   * moving the centre, which is why the geometric box is the honest thing to measure.
   */
  play: 'M7 5.5l10 6.5-10 6.5z',
  /**
   * THE ATTACHMENT GLYPH (`docs/decisions/file-attachments.md` D18), and it obeys this
   * file's centring rule by construction rather than by measurement: the outer clip's
   * bottom semicircle reaches y=19, the inner one's top reaches y=5, and the two verticals
   * sit at x=8.5 and x=15.5 - so the geometric box is 8.5..15.5 by 5..19, centred exactly
   * on (12,12).
   */
  paperclip: 'M15.5 8.5v7a3.5 3.5 0 0 1-7 0V7a2 2 0 0 1 4 0v8a.5.5 0 0 1-1 0V8.5',
} as const;

export type IconName = keyof typeof ICON_PATHS;

export const ICON_NAMES = Object.keys(ICON_PATHS) as readonly IconName[];

export interface IconProps {
  readonly name: IconName;
  /** Any CSS length. Defaults to 1em so an icon follows its text. */
  readonly size?: string | undefined;
  readonly className?: string | undefined;
}

export function Icon({ name, size = '1.25em', className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      /**
       * `block`, and it is the other half of the centring fix. An <svg> is
       * inline by default, so in any context that is NOT a flex container it
       * sits on the TEXT BASELINE with the line box's descender space below it -
       * which drops the glyph a couple of pixels below the optical centre of the
       * text beside it. Every place that had a hand-tuned `mt-0.5` was paying
       * for that. Blockifying costs nothing where a flex parent already
       * blockified it, and fixes the rest.
       */
      className={cx('block shrink-0', className)}
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}
